// src/components/directory/ChatSidebar.jsx
// Covers: PL-06 (AI chat sidebar using Claude API)

import React, { useRef, useEffect, useState } from 'react';
import { track, EVENTS } from '../../lib/firebase';
import styles from './ChatSidebar.module.css';

const QUICK_PROMPTS = [
  'PTSD support for my spouse',
  'Free programs, no referral needed',
  'Help after medical release',
  'Rural mental health options',
];

export default function ChatSidebar({ chat, wizardAnswers }) {
  const { messages, loading, error, sendMessage } = chat;
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);

  // Scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSend = () => {
    if (!inputValue.trim() || loading) return;
    sendMessage(inputValue.trim());
    setInputValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickPrompt = (prompt) => {
    track(EVENTS.AI_CHAT_OPENED, { source: 'quick_prompt' });
    sendMessage(prompt);
    inputRef.current?.focus();
  };

  return (
    <div className={styles.sidebar}>

      {/* Chat box */}
      <div className={styles.chatBox}>
        <div className={styles.chatHeader}>
          <span className={styles.dot} />
          <div>
            <div className={styles.chatTitle}>Signal Post AI</div>
            <div className={styles.chatSubtitle}>Resource guidance · Available now</div>
          </div>
        </div>

        {/* Messages */}
        <div className={styles.messages}>
          {/* Initial greeting */}
          {messages.length === 0 && (
            <div className={styles.message}>
              <div className={styles.msgWho}>Signal Post AI</div>
              <div className={`${styles.msgBubble} ${styles.ai}`}>
                Hi. I can help you find the right programs for your situation — for you or someone you care about.{' '}
                <strong>What are you looking for today?</strong>
              </div>
            </div>
          )}

          {/* Chat history */}
          {messages.map((m, i) => (
            <div
              key={i}
              className={`${styles.message} ${m.role === 'user' ? styles.userMsg : ''}`}
            >
              <div className={styles.msgWho}>
                {m.role === 'user' ? 'You' : 'Signal Post AI'}
              </div>
              <div className={`${styles.msgBubble} ${m.role === 'user' ? styles.user : styles.ai}`}>
                {/* Render line breaks from AI response */}
                {m.content.split('\n').map((line, j) => (
                  <React.Fragment key={j}>
                    {line}
                    {j < m.content.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {loading && (
            <div className={styles.message}>
              <div className={styles.msgWho}>Signal Post AI</div>
              <div className={`${styles.msgBubble} ${styles.ai}`}>
                <div className={styles.typing}>
                  <span /><span /><span />
                </div>
              </div>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className={styles.errorMsg}>{error}</div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Quick prompts — shown only before first message */}
        {messages.length === 0 && (
          <div className={styles.quickPrompts}>
            <div className={styles.quickLabel}>Try asking</div>
            {QUICK_PROMPTS.map(p => (
              <button
                key={p}
                className={styles.quickBtn}
                onClick={() => handleQuickPrompt(p)}
              >
                {p}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className={styles.inputRow}>
          <textarea
            ref={inputRef}
            className={styles.input}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe what you need…"
            rows={1}
            disabled={loading}
          />
          <button
            className={styles.sendBtn}
            onClick={handleSend}
            disabled={!inputValue.trim() || loading}
            aria-label="Send message"
          >
            →
          </button>
        </div>
      </div>

      {/* Submit panel */}
      <div className={styles.panel}>
        <div className={styles.panelTitle}>Know a program we're missing?</div>
        <p className={styles.panelBody}>
          Signal Post is community-maintained. Submit a program and our team will verify and add it.
        </p>
        <button
          className={styles.submitBtn}
          onClick={() => {/* TODO: open submit modal */}}
        >
          + Submit a program
        </button>
        {/* Discreet crisis reference */}
        <div className={styles.crisisNote}>
          <strong>Need immediate support?</strong><br />
          VAC / CAF: <a href="tel:18002687708">1-800-268-7708</a>
          {' · '}
          Talk Suicide: <a href="tel:18334564566">1-833-456-4566</a>
        </div>
      </div>

    </div>
  );
}
